import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, tap, switchMap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListResponse, ISnListQueryParameters } from '@lib/api';
import { SnOperationalView, SnWidget, SnWidgetMedia } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnOperationalViewsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getViewList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnOperationalView>> {
    return this._httpClient.get('/api/operational-views/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((view) => new SnOperationalView(view)),
      })),
    );
  }

  public createView(data: any): Observable<SnOperationalView> {
    return this._httpClient.post('/api/operational-views/', data).pipe(
      map((response: any) => new SnOperationalView(response)),
      tap({
        next: () => this._toastrService.success(t('dashboard.operational.notifications.viewCreated')),
        error: () => this._toastrService.error(t('dashboard.operational.errors.couldNotCreateView')),
      }),
    );
  }

  public getView(id: string): Observable<SnOperationalView> {
    return this._httpClient
      .get(`/api/operational-views/${id}/`)
      .pipe(map((response: any) => new SnOperationalView(response)));
  }

  public getViewWidgets(id: string): Observable<ISnListResponse<SnWidget>> {
    return this._httpClient.get<ISnListResponse<SnWidget>>(
      `/api/operational-views/${id}/widgets/`,
      {
        params: { pageSize: 'all' },
      },
    );
  }

  public updateView(id: string, data: any): Observable<SnOperationalView> {
    return this._httpClient.patch(`/api/operational-views/${id}/`, data).pipe(
      map((response: any) => new SnOperationalView(response)),
      tap({
        next: () => this._toastrService.success(t('dashboard.operational.notifications.viewUpdated')),
        error: () => this._toastrService.error(t('dashboard.operational.errors.couldNotUpdateView')),
      }),
    );
  }

  public deleteView(view: SnOperationalView): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('dashboard.operational.dialogs.confirmDeleteView'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/operational-views/${view.id}/`)),
        tap({
          next: () => this._toastrService.success(t('dashboard.operational.notifications.viewDeleted')),
          error: () =>
            this._toastrService.error(
              t('dashboard.operational.errors.couldNotDeleteView'),
            ),
        }),
      );
  }

  public getViewWidgetList(
    id: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnWidget>> {
    return this._httpClient.get(`/api/operational-views/${id}/widgets/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((widget) => new SnWidget(widget)),
      })),
    );
  }

  public createViewWidget(id: string, data: any): Observable<SnWidget> {
    return this._httpClient.post(`/api/operational-views/${id}/widgets/`, data).pipe(
      map((response: any) => new SnWidget(response)),
      tap({
        next: () => this._toastrService.success(t('dashboard.operational.notifications.widgetCreated')),
        error: () => this._toastrService.error(t('dashboard.operational.errors.couldNotCreateWidget')),
      }),
    );
  }

  public getViewWidget(viewId: string, widgetId: string): Observable<SnWidget> {
    return this._httpClient
      .get(`/api/operational-views/${viewId}/widgets/${widgetId}/`)
      .pipe(map((response: Object) => new SnWidget(response)));
  }

  public updateViewWidget(viewId: string, widgetId: string, data: any): Observable<SnWidget> {
    return this._httpClient
      .patch(`/api/operational-views/${viewId}/widgets/${widgetId}/`, data)
      .pipe(
        map((response: any) => new SnWidget(response)),
        tap({
          next: () => this._toastrService.success(t('dashboard.operational.notifications.widgetUpdated')),
          error: () => this._toastrService.error(t('dashboard.operational.errors.couldNotUpdateWidget')),
        }),
      );
  }

  public deleteViewWidget(viewId: string, widget: SnWidget): Observable<Object> {
    return this._httpClient.delete(`/api/operational-views/${viewId}/widgets/${widget.id}/`).pipe(
      tap({
        next: () => this._toastrService.success(t('dashboard.operational.notifications.widgetDeleted')),
        error: () => this._toastrService.error(t('dashboard.operational.errors.couldNotDeleteWidget')),
      }),
    );
  }

  public createViewWidgetMedia(id: string, data: any): Observable<SnWidgetMedia> {
    return this._httpClient.post(`/api/operational-views/${id}/widgets/`, data).pipe(
      map((response: any) => new SnWidgetMedia(response)),
      tap({
        next: () => this._toastrService.success(t('dashboard.operational.notifications.mediaCreated')),
        error: () => this._toastrService.error(t('dashboard.operational.errors.couldNotCreateMedia')),
      }),
    );
  }

  public deleteViewWidgetMedia(
    viewId: string,
    widgetId: string,
    mediaId: string,
  ): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('dashboard.operational.dialogs.confirmDeleteMedia'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() =>
          this._httpClient.delete(
            `/api/operational-views/${viewId}/widgets/${widgetId}/media/${mediaId}/`,
          ),
        ),
        tap({
          next: () => this._toastrService.success(t('dashboard.operational.notifications.mediaDeleted')),
          error: () =>
            this._toastrService.error(
              t('dashboard.operational.errors.couldNotDeleteMedia'),
            ),
        }),
      );
  }
}
